import {
  Button,
  Col,
  DatePicker,
  Form,
  message,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
} from 'antd';

import { useCallback, useEffect, useState } from 'react';
import { IAllInvoices } from '../../../../common/types/commonInterfaces';
import {
  useDeleteAirticketMutation,
  useGetAllInvoicesV2Query,
} from '../../api/endpoints/airticketInvoiceEndpoints';

import { DollarOutlined, PlusOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/es/table';
import { TimeRangePickerProps } from 'antd/lib';
import dayjs from 'dayjs';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectUser } from '../../../../auth/states/userSlice';
import { crud, IPermission } from '../../../../common/types/commonTypes';
import { debounce, formatAmount, separateClient } from '../../../../common/utils/common.utils';
import usePagination from '../../../../common/utils/usePagination';
import useUrlParams from '../../../../common/utils/useUrlParams';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { FormInput } from '../../../../components/common/FormItem/FormItems';
import Reload from '../../../../components/common/reload/Reload';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import { formatDateRangeArray, parseDateRangeObject } from '../../../../helpers/healper';
import InvoiceOtherAddMoneyModal from '../../../Invoice(Other)/Modals/InvoiceOtherAddMoneyModal';
import AddClientListInvoice from './AddClientListInvoice';

type Props = {
  permission?: IPermission;
  addMRPermission: ['*'] | undefined;
};

const { Text } = Typography;

const ListOfInvoiceAirticket = ({ permission, addMRPermission }: Props) => {
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };
  const [columnIndex, setColumnIndex] = useState<number | null>(null);
  const user = useSelector(selectUser);
  const [openModal, setOpenModal] = useState(false);
  const [invoiceInfo, setInvoiceInfo] = useState<IAllInvoices>();
  const [addClientModal, setAddClientModal] = useState(false);

  // LINKS
  const createLink = '/airticket/add';
  const viewLink = '/airticket/details';
  const editLink = '/airticket/edit';

  const {
    data,
    isFetching,
    refetch: getAllRefetch,
    isLoading: dataIsLoading,
  } = useGetAllInvoicesV2Query({
    ...getAllUrlParams(),
    page,
    size,
  });

  const [deleteInvoice, { isError, isSuccess, isLoading, error }] = useDeleteAirticketMutation();

  // DELETE HANDLER
  const handleOnDelete = (id: number, index: number) => {
    setColumnIndex(index);
    deleteInvoice({
      id,
      user_id: user?.user_id,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      setColumnIndex(null);
      message.success('Invoice Deleted Successfully!');
    }
    if (isError) {
      setColumnIndex(null);
      message.error('There was an error');
    }
  }, [isError, isSuccess]);

  const ModalOperation = (data: IAllInvoices) => {
    setInvoiceInfo(data);
    setOpenModal(!openModal);
  };

  const columns: ColumnsType<IAllInvoices> = [
    {
      title: 'SL.',
      width: 55,
      render: (_, data, index) => (
        <>{((pagination?.current || 1) - 1) * (pagination?.pageSize || 50) + 1 + index}</>
      ),
    },
    {
      title: 'Invoice No',
      dataIndex: 'invoice_no',
      key: 'invoice_no',
      width: 100,
      render: (value) => value || 'N/A', // Add null check
    },
    {
      title: 'Invoice Type',
      dataIndex: 'invoice_reissue_client_type',
      key: 'invoice_reissue_client_type',
      width: 80,
      render: (item) => {
        let color = 'red';
        if (item === 'IUR') {
          color = 'cyan';
        } else if (item === 'PNR') {
          color = 'purple';
        } else {
          color = 'pink';
        }
        return <Tag color={color}>{item || 'Not Available'}</Tag>;
      },
    },
    {
      title: 'Sales Date',
      key: 'invoice_date',
      dataIndex: 'invoice_date',
      render: (_, data) => {
        // Add null/undefined check for date
        if (!data?.invoice_date) return 'N/A';
        const date = moment(data.invoice_date);
        return date.isValid() ? date.format('DD MMM YYYY') : 'Invalid Date';
      },
      width: 135,
    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (value, record) => {
        // Add null checks
        if (!record?.comb_client) {
          return record?.client_name || 'N/A';
        }

        const { client_id, combined_id } = separateClient(record.comb_client);

        return (
          <Link
            to={
              client_id
                ? `/reports/client_ledger?client_query=${client_id}`
                : `/reports/combined_ledger?id=${combined_id}`
            }
          >
            <span>
              {record.client_name || 'N/A'} / {record.client_entry_id || 'N/A'}
            </span>
          </Link>
        );
      },
      width: 150,
    },
    {
      title: 'Sales Price',
      dataIndex: 'net_total',
      key: 'net_total',
      width: 120,
      align: 'right',
      sorter: (a, b) => Number(a.net_total || 0) - Number(b.net_total || 0),
      render: (curr) => <span className='color-sales'>{formatAmount(curr || 0)}</span>,
    },
    {
      title: 'Rec Amount',
      dataIndex: 'invclientpayment_amount',
      key: 'invclientpayment_amount',
      align: 'right',
      width: 120,
      render: (curr) => <span className='color-collection'>{formatAmount(curr || 0)}</span>,
    },
    {
      title: 'Due Amount',
      align: 'right',
      width: 120,
      render(_, record) {
        const netTotal = Number(record?.net_total || 0);
        const paidAmount = Number(record?.invclientpayment_amount || 0);
        const dueAmount = netTotal - paidAmount;

        return dueAmount > 0 ? (
          <span style={{ color: 'red' }}>{formatAmount(dueAmount)}</span>
        ) : (
          <Tag color='cyan' icon={<DollarOutlined />}>
            PAID
          </Tag>
        );
      },
      sorter: (a, b) => {
        const aDue = Number(a.net_total || 0) - Number(a.invclientpayment_amount || 0);
        const bDue = Number(b.net_total || 0) - Number(b.invclientpayment_amount || 0);
        return aDue - bDue;
      },
    },
    {
      title: 'MR. No.',
      dataIndex: 'money_receipt_num',
      key: 'money_receipt_num',
      width: 120,
      render: (_, data) => {
        // Add null check for money_receipt_num
        if (!data?.money_receipt_num) return 'N/A';

        const receipts = data.money_receipt_num.split(',');
        return (
          <span>
            {receipts.slice(0, 3).map((item, index) => (
              <li key={index}>{item.trim()}</li>
            ))}
            {receipts.length > 3 && <span>...</span>}
          </span>
        );
      },
    },
    {
      title: 'Sales by',
      dataIndex: 'sales_by',
      key: 'sales_by',
      width: 150,
      render: (value) => value || 'N/A', // Add null check
    },
    {
      title: 'Action',
      key: 'operation',
      width: 200,
      render: (_, data, index) => {
        return (
          <Space size='small'>
            {permission?.['read:any'] && (
              <Link to={viewLink + '/' + data?.invoice_id} state={location.pathname}>
                <Button size='small' type='primary'>
                  <Text style={{ color: '#ffffff' }}>View</Text>
                </Button>
              </Link>
            )}

            {!data.sales_by || !data.comb_client ? (
              <Button
                onClick={() => {
                  setInvoiceInfo(data);
                  setAddClientModal(!addClientModal);
                }}
                size='small'
                danger
              >
                Add Info
              </Button>
            ) : (
              <>
                {!data?.invoice_is_reissued &&
                permission?.[crud.update] &&
                !data?.invoice_is_refund ? (
                  <Link to={editLink + '/' + data?.invoice_id} state={location.pathname}>
                    <Button size='small' type='primary'>
                      Edit
                    </Button>
                  </Link>
                ) : (
                  <Button size='small' type='primary' disabled>
                    Edit
                  </Button>
                )}

                {permission?.['delete:any'] && (
                  <Button
                    size='small'
                    type='primary'
                    danger
                    loading={columnIndex === index && isLoading}
                    disabled={!!data?.invoice_is_reissued || !!data?.invoice_is_refund}
                  >
                    <Popconfirm
                      onConfirm={() => handleOnDelete(data?.invoice_id, index)}
                      title='Sure to delete?'
                    >
                      <Typography style={{ color: '#ffffff' }}>Delete</Typography>
                    </Popconfirm>
                  </Button>
                )}
                {addMRPermission && (
                  <Button
                    disabled={
                      Number(data?.net_total || 0) <= Number(data?.invclientpayment_amount || 0) ||
                      !!data?.invoice_is_refund
                    }
                    onClick={(e) => ModalOperation(data)}
                    size='small'
                    type='primary'
                  >
                    Add Money
                  </Button>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  // Add safety check for data mapping
  const dataWithKey =
    data?.data?.map((item, index) => ({
      key: String(index),
      ...item,
    })) || [];

  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search || '',
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);
  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Today', value: [dayjs(), dayjs()] },
    { label: 'Yesterday', value: [dayjs().add(-1, 'd'), dayjs()] },
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ];
  return (
    <>
      <BreadCrumb arrOfOption={['Invoice', 'Air Ticket']} />

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 10 }} justify={'space-between'}>
        <Col span={12}>
          <Row gutter={[10, 10]}>
            {permission?.['create:any'] && (
              <Col xs={24} sm={12} md={8} lg={8}>
                <Link to={createLink}>
                  <Button type='primary' icon={<PlusOutlined />} style={{ width: '100%' }}>
                    Create
                  </Button>
                </Link>
              </Col>
            )}

            <Col xs={24} sm={12} md={8} lg={8}>
              <Col>
                <Reload
                  refetch={() => {
                    getAllRefetch();
                  }}
                />
              </Col>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <Col xs={24} sm={12} md={8} lg={8}>
                <DatePicker.RangePicker
                  name={'date_range'}
                  presets={rangePresets}
                  format={'YYYY-MM-DD'}
                  onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
                />
              </Col>
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
            </Row>
          </Form>
        </Col>
      </Row>

      {invoiceInfo?.invoice_id && (
        <InvoiceOtherAddMoneyModal
          invoice_id={invoiceInfo?.invoice_id}
          InvocieModalTitle={'Invoice Air Ticket'}
          disabled={false}
          setOpenModal={setOpenModal}
          openModal={openModal}
        />
      )}

      <AddClientListInvoice
        setAddClientModal={setAddClientModal}
        addClientModal={addClientModal}
        invoiceInfo={invoiceInfo}
      />

      <div>
        <span style={{ marginLeft: '5px', float: 'left', marginBottom: '1rem' }}>
          You have total <strong>{data?.count || data?.data?.length || 0}</strong> rows.
        </span>
        <Table
          size='small'
          bordered
          rowClassName={(record) => {
            const {
              invoice_is_reissued: reissue,
              invoice_is_refund: refund,
              invoice_is_void: void_inv,
            } = record || {};
            let className = '';

            if (void_inv) return (className = 'void_inv-row-style');
            if (reissue) return (className = 'reissued-row-style');
            if (refund) return (className = 'refund-row-style');

            return className;
          }}
          loading={{
            spinning: dataIsLoading || isFetching,
            indicator: loadingIndicator,
          }}
          columns={columns}
          scroll={{ x: 'max-content' }}
          dataSource={dataWithKey}
          pagination={changePagination(data?.count)}
        />
      </div>
    </>
  );
};

export default ListOfInvoiceAirticket;
